import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Hit } from '@pf/shared';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';

export interface HitsParams {
  page?: number;
  limit?: number;
  clientId?: string;
  pressListId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CreateHitBody {
  url: string;
  title: string;
  publicationName: string;
  publicationUrl?: string | null;
  clientId?: string | null;
  pressListId?: string | null;
  contactId?: string | null;
  reporterId?: string | null;
  publishedAt?: string | null;
  notes?: string | null;
}

export type UpdateHitBody = Partial<CreateHitBody>;

export interface HitsResponse {
  hits: Hit[];
  total: number;
  page: number;
  limit: number;
}

export interface PrefillResult {
  title: string | null;
  publicationName: string | null;
  url: string;
}

const HITS_KEY = 'hits';

export function useHits(params: HitsParams = {}) {
  return useQuery({
    queryKey: [HITS_KEY, params],
    queryFn: () =>
      apiGet<HitsResponse>('/hits', {
        page: params.page ?? 1,
        limit: params.limit ?? 50,
        clientId: params.clientId,
        pressListId: params.pressListId,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      }),
  });
}

export function useHit(id: string | undefined) {
  return useQuery({
    queryKey: [HITS_KEY, id],
    queryFn: () => apiGet<{ hit: Hit }>(`/hits/${id}`),
    enabled: !!id,
  });
}

export function useCreateHit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHitBody) => apiPost<{ hit: Hit }>('/hits', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HITS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateHit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHitBody }) =>
      apiPut<{ hit: Hit }>(`/hits/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [HITS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HITS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useDeleteHit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/hits/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HITS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function usePrefillHit() {
  return useMutation({
    mutationFn: (url: string) => apiPost<PrefillResult>('/hits/prefill', { url }),
  });
}
